import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { uploadFile, validateFile } from '../services/s3UploadService';

/**
 * S3FileUpload Component
 * A reusable component for uploading files to S3 with fallback to local upload
 */
const S3FileUpload = ({
  onUploadComplete,
  onUploadError,
  acceptedTypes = 'image/*,video/*',
  maxSize = Infinity, // No size limit
  folder = 'offer-templates',
  buttonText = 'Choose File',
  buttonVariant = 'outlined',
  buttonSize = 'medium',
  showProgress = true,
  showPreview = false,
  disabled = false,
  fullWidth = false,
  startIcon = <CloudUploadIcon />
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadMethod, setUploadMethod] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setError(null);
    setProgress(0);
    setUploadMethod(null);

    try {
      // Validate file size and type before upload
      validateFile(file, {
        maxSize,
        allowedTypes: acceptedTypes.split(',').map(type => type.trim().replace('*', ''))
      });

      setUploading(true);

      // Upload file
      const result = await uploadFile(file, folder, (progressPercent) => {
        setProgress(progressPercent);
      });

      setUploadMethod(result.method);
      console.log(`✅ File uploaded successfully via ${result.method}:`, result.url);

      // Call success callback
      if (onUploadComplete) {
        onUploadComplete({
          url: result.url,
          file,
          method: result.method
        });
      }

    } catch (err) {
      console.error('❌ Upload error:', err);
      const errorMessage = err.message || 'Upload failed. Please try again.';
      setError(errorMessage);
      
      // Call error callback
      if (onUploadError) {
        onUploadError(err);
      }
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  }, [acceptedTypes, maxSize, folder, onUploadComplete, onUploadError]);

  const getFileIcon = (file) => {
    if (!file) return <CloudUploadIcon />;
    
    if (file.type.startsWith('image/')) {
      return <ImageIcon />;
    } else if (file.type.startsWith('video/')) {
      return <VideoIcon />;
    }
    return <CloudUploadIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Upload Button */}
      <Button
        variant={buttonVariant}
        component="label"
        size={buttonSize}
        startIcon={uploading ? <CircularProgress size={16} /> : startIcon}
        disabled={disabled || uploading}
        fullWidth={fullWidth}
        sx={{ mb: 1 }}
      >
        {uploading ? 'Uploading...' : buttonText}
        <input
          type="file"
          hidden
          accept={acceptedTypes}
          onChange={handleFileSelect}
          disabled={disabled || uploading}
        />
      </Button>

      {/* Selected File Info */}
      {selectedFile && !uploading && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
          </Typography>
        </Box>
      )}

      {/* Progress Bar */}
      {uploading && showProgress && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Uploading {selectedFile?.name}...
          </Typography>
        </Box>
      )}

      {/* Upload Method Indicator */}
      {uploadMethod && !uploading && (
        <Box sx={{ mb: 1 }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`Uploaded via ${uploadMethod.toUpperCase()}`}
            color="success"
            size="small"
            variant="outlined"
          />
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* File Preview (if enabled and image) */}
      {showPreview && selectedFile && selectedFile.type.startsWith('image/') && !uploading && (
        <Box sx={{ mt: 1 }}>
          <img
            src={URL.createObjectURL(selectedFile)}
            alt="Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              objectFit: 'contain',
              borderRadius: '4px',
              border: '1px solid #ddd'
            }}
          />
        </Box>
      )}

      {/* Helper Text */}
      {/* <Typography variant="caption" color="text.secondary" display="block">
        Max size: {formatFileSize(maxSize)}. Accepted: {acceptedTypes}
      </Typography> */}
    </Box>
  );
};

export default S3FileUpload;
